import React from "react";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Info, AlertTriangle, MessageSquare, Activity, Heart, Pill, AlertCircle, ThermometerSun, CheckCircle2, Clock, MapPin } from "lucide-react";
import { TriageData } from "../TriageWizard";
import { useLanguage } from "@/contexts/LanguageContext";

interface ReviewStepProps {
  data: TriageData;
  updateData: (data: TriageData) => void;
  prefilled?: boolean;
}

const ReviewStep: React.FC<ReviewStepProps> = ({ data }) => {
  const { t } = useLanguage();
  const hasEmergencySymptoms = data.emergency_symptoms.length > 0;
  const hasSevereSymptoms = data.emergency_symptoms.some(s => 
    ["severe_bleeding", "unconscious", "difficulty_breathing_severe", "chest_pain_severe", "neurological"].includes(s)
  );

  const formatSymptom = (symptom: string) => symptom.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

  const getPainSeverityColor = (level: number) => {
    if (level <= 3) return "text-emerald-600 bg-emerald-500/10";
    if (level <= 6) return "text-amber-600 bg-amber-500/10";
    return "text-red-600 bg-red-500/10";
  };

  const getPainSeverityLabel = (level: number) => {
    if (level <= 3) return t.review.mild;
    if (level <= 6) return t.review.moderate;
    return t.review.severe;
  };

  const completedSections = [
    data.chief_complaint.length > 0,
    data.symptoms.length > 0,
    data.medical_history.length > 0 || data.medications.length > 0 || data.allergies.length > 0,
    true,
  ].filter(Boolean).length;

  return (
    <div className="space-y-6">
      <div className="text-center pb-2">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-3">
          <CheckCircle2 className="h-6 w-6 text-primary" />
        </div>
        <Label className="text-xl font-semibold block">{t.review.title}</Label>
        <p className="text-sm text-muted-foreground mt-2">{t.review.subtitle}</p>
      </div>

      <Card className="bg-muted/30 border-dashed">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              <span>{completedSections} {t.review.sectionsCompleted}</span>
            </div>
            <p className="text-xs text-muted-foreground">{t.review.editHint}</p>
          </div>
        </CardContent>
      </Card>

      {hasSevereSymptoms && (
        <Alert className="border-red-300 bg-red-50 dark:bg-red-950/20 animate-pulse">
          <AlertTriangle className="h-5 w-5 text-red-600" />
          <AlertDescription className="text-red-800 dark:text-red-300">{t.review.emergencyAlert}</AlertDescription>
        </Alert>
      )}

      <div className="space-y-4">
        <Card className="overflow-hidden">
          <CardHeader className="bg-muted/30 py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10"><MessageSquare className="h-4 w-4 text-primary" /></div>
              {t.review.step1Title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {data.chief_complaint ? <p className="text-sm leading-relaxed">{data.chief_complaint}</p> : <p className="text-sm text-muted-foreground italic">{t.review.notSpecified}</p>}
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="bg-muted/30 py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10"><Activity className="h-4 w-4 text-primary" /></div>
              {t.review.step2Title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {data.symptoms.length > 0 ? (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t.review.selectedSymptoms}</p>
                <div className="flex flex-wrap gap-2">
                  {data.symptoms.map((s) => <Badge key={s} variant="secondary" className="text-xs">{formatSymptom(s)}</Badge>)}
                </div>
              </div>
            ) : <p className="text-sm text-muted-foreground italic">{t.review.noSymptomsSelected}</p>}
            {data.pain_severity > 0 && (
              <>
                <Separator />
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">{t.review.painLevel}</span>
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${getPainSeverityColor(data.pain_severity)}`}>
                    {data.pain_severity}/10 ({getPainSeverityLabel(data.pain_severity)})
                  </div>
                </div>
              </>
            )}
            {(data.symptom_onset || data.location) && (
              <>
                <Separator />
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {data.symptom_onset && <div className="flex items-start gap-2"><Clock className="h-4 w-4 text-muted-foreground mt-0.5" /><div><p className="text-xs text-muted-foreground">{t.review.onset}</p><p className="font-medium">{formatSymptom(data.symptom_onset)}</p></div></div>}
                  {data.location && <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-muted-foreground mt-0.5" /><div><p className="text-xs text-muted-foreground">{t.review.location}</p><p className="font-medium">{formatSymptom(data.location)}</p></div></div>}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="bg-muted/30 py-3">
            <CardTitle className="text-base flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-primary/10"><Heart className="h-4 w-4 text-primary" /></div>
              {t.review.step3Title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {(data.vitals.temperature || data.vitals.blood_pressure || data.vitals.heart_rate) && (
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">{t.review.vitalSigns}</p>
                <div className="grid grid-cols-3 gap-3">
                  {data.vitals.temperature && <div className="bg-muted/50 rounded-lg p-3 text-center"><ThermometerSun className="h-4 w-4 mx-auto mb-1 text-muted-foreground" /><p className="text-lg font-semibold">{data.vitals.temperature}°F</p><p className="text-xs text-muted-foreground">{t.review.temp}</p></div>}
                  {data.vitals.blood_pressure && <div className="bg-muted/50 rounded-lg p-3 text-center"><Activity className="h-4 w-4 mx-auto mb-1 text-muted-foreground" /><p className="text-lg font-semibold">{data.vitals.blood_pressure}</p><p className="text-xs text-muted-foreground">{t.review.bp}</p></div>}
                  {data.vitals.heart_rate && <div className="bg-muted/50 rounded-lg p-3 text-center"><Heart className="h-4 w-4 mx-auto mb-1 text-muted-foreground" /><p className="text-lg font-semibold">{data.vitals.heart_rate}</p><p className="text-xs text-muted-foreground">{t.review.bpm}</p></div>}
                </div>
              </div>
            )}
            {data.medical_history.length > 0 && <div><p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{t.review.medicalConditions}</p><div className="flex flex-wrap gap-2">{data.medical_history.map((c) => <Badge key={c} variant="outline" className="text-xs">{formatSymptom(c)}</Badge>)}</div></div>}
            {data.medications.length > 0 && <div><div className="flex items-center gap-2 mb-2"><Pill className="h-3.5 w-3.5 text-muted-foreground" /><p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t.review.currentMedications}</p></div><p className="text-sm">{data.medications.join(", ")}</p></div>}
            {data.allergies.length > 0 && <div><div className="flex items-center gap-2 mb-2"><AlertCircle className="h-3.5 w-3.5 text-amber-500" /><p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t.review.allergies}</p></div><div className="flex flex-wrap gap-2">{data.allergies.map((a, i) => <Badge key={i} variant="outline" className="text-xs border-amber-300 text-amber-700 dark:text-amber-400">{a}</Badge>)}</div></div>}
            {data.pregnancy && <div className="flex items-center gap-2 p-2 bg-pink-50 dark:bg-pink-950/20 rounded-lg"><Badge variant="secondary" className="bg-pink-100 text-pink-700 dark:bg-pink-900 dark:text-pink-300">{t.review.pregnant}</Badge></div>}
            {!data.vitals.temperature && !data.vitals.blood_pressure && !data.vitals.heart_rate && data.medical_history.length === 0 && data.medications.length === 0 && data.allergies.length === 0 && !data.pregnancy && <p className="text-sm text-muted-foreground italic">{t.review.noVitalsProvided}</p>}
          </CardContent>
        </Card>

        <Card className={`overflow-hidden ${hasEmergencySymptoms ? 'border-orange-300 dark:border-orange-700' : ''}`}>
          <CardHeader className={`py-3 ${hasEmergencySymptoms ? 'bg-orange-50 dark:bg-orange-950/20' : 'bg-muted/30'}`}>
            <CardTitle className={`text-base flex items-center gap-2 ${hasEmergencySymptoms ? 'text-orange-800 dark:text-orange-300' : ''}`}>
              <div className={`p-1.5 rounded-lg ${hasEmergencySymptoms ? 'bg-orange-100 dark:bg-orange-900' : 'bg-primary/10'}`}><AlertTriangle className={`h-4 w-4 ${hasEmergencySymptoms ? 'text-orange-600' : 'text-primary'}`} /></div>
              {t.review.step4Title}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4">
            {hasEmergencySymptoms ? (
              <div className="flex flex-wrap gap-2">{data.emergency_symptoms.map((s) => <Badge key={s} variant="destructive" className="text-xs">{formatSymptom(s)}</Badge>)}</div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="h-4 w-4" /><span>{t.review.noEmergencySigns}</span></div>
            )}
          </CardContent>
        </Card>
      </div>

      <Alert className="bg-muted/30">
        <Info className="h-4 w-4" />
        <AlertDescription className="text-sm">{t.review.disclaimer}</AlertDescription>
      </Alert>
    </div>
  );
};

export default ReviewStep;
